import { useState } from 'react'
import { NavLink, Outlet, Link } from 'react-router-dom'
import { Logo } from './Logo'
import { useCurrentUser } from '../../lib/session'

interface NavItem {
  to: string
  label: string
  /** Only shown to Site Admins. */
  siteAdminOnly?: boolean
}

const navigation: NavItem[] = [
  { to: '/app', label: 'Overview' },
  { to: '/app/subscriptions', label: 'Subscriptions' },
  { to: '/app/routes', label: 'Routes' },
  { to: '/app/scouts', label: 'Scouts' },
  { to: '/app/holidays', label: 'Holidays' },
  { to: '/app/service-areas', label: 'Service areas' },
  { to: '/app/messages', label: 'Messages' },
  { to: '/app/site', label: 'Site admin', siteAdminOnly: true },
]

function NavItems({ isSiteAdmin, onNavigate }: { isSiteAdmin: boolean; onNavigate?: () => void }) {
  return (
    <>
      {navigation
        .filter((item) => !item.siteAdminOnly || isSiteAdmin)
        .map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            // Only the overview should match by prefix; the rest are leaf routes.
            end={item.to === '/app'}
            onClick={onNavigate}
            className={({ isActive }) =>
              [
                'block rounded-lg px-3 py-2 text-sm font-medium transition',
                isActive
                  ? 'bg-forest-700 text-white'
                  : 'text-[color:var(--text-muted)] hover:bg-[color:var(--surface-sunken)] hover:text-[color:var(--text-strong)]',
              ].join(' ')
            }
          >
            {item.label}
          </NavLink>
        ))}
    </>
  )
}

/**
 * Chrome for signed-in staff. A persistent sidebar on desktop collapses to a slide-over
 * drawer under `md`, because a Unit Admin checking signups from a phone is the common case.
 */
export function AppLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { data: user } = useCurrentUser()
  const isSiteAdmin = user?.isSiteAdmin ?? false

  return (
    <div className="min-h-dvh md:grid md:grid-cols-[16rem_1fr]">
      <aside className="hidden border-r border-[color:var(--surface-border)] bg-[color:var(--surface-raised)] md:block">
        <div className="sticky top-0 flex h-dvh flex-col">
          <Link
            to="/app"
            className="flex h-16 items-center gap-2.5 px-4 text-forest-700 dark:text-forest-200"
          >
            <Logo />
            <span className="font-semibold tracking-tight text-[color:var(--text-strong)]">
              Holiday Flags
            </span>
          </Link>

          <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2" aria-label="Unit">
            <NavItems isSiteAdmin={isSiteAdmin} />
          </nav>

          <div className="border-t border-[color:var(--surface-border)] p-4">
            <p className="truncate text-sm font-medium">{user?.displayName ?? 'Signed out'}</p>
            <p className="truncate text-xs text-[color:var(--text-muted)]">{user?.email}</p>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-[color:var(--surface-border)] bg-[color:var(--surface-raised)]/85 px-4 backdrop-blur md:hidden">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="-ml-1 rounded-lg p-2 hover:bg-[color:var(--surface-sunken)]"
            aria-label="Open navigation"
            aria-expanded={drawerOpen}
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" />
            </svg>
          </button>
          <Link to="/app" className="flex items-center gap-2 text-forest-700 dark:text-forest-200">
            <Logo className="h-6 w-6" />
            <span className="font-semibold text-[color:var(--text-strong)]">Holiday Flags</span>
          </Link>
        </header>

        {drawerOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <button
              type="button"
              className="absolute inset-0 bg-forest-950/40"
              aria-label="Close navigation"
              onClick={() => setDrawerOpen(false)}
            />
            <div className="absolute inset-y-0 left-0 w-64 bg-[color:var(--surface-raised)] p-3 shadow-lifted">
              <nav className="space-y-1 pt-2" aria-label="Unit">
                <NavItems isSiteAdmin={isSiteAdmin} onNavigate={() => setDrawerOpen(false)} />
              </nav>
            </div>
          </div>
        )}

        <main className="min-w-0 flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

interface PageHeaderProps {
  title: string
  description?: string
  action?: React.ReactNode
}

export function PageHeader({ title, description, action }: PageHeaderProps) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-1 text-sm text-[color:var(--text-muted)]">{description}</p>}
      </div>
      {action}
    </div>
  )
}
